import { useMemo } from 'react'
import { Grid, Paper, Text, Group, Stack, Badge } from '@mantine/core'
import {
  IconUsers,
  IconUserPlus,
  IconMoodKid,
  IconMail,
  IconClipboardList,
} from '@tabler/icons-react'
import { useGuestStats } from '../../hooks/useGuestStats'
import { useGuests } from '../../hooks/useGuests'
import styles from './GuestStats.module.css'

export function GuestStats() {
  const { stats, loading: statsLoading } = useGuestStats()
  const { guests = [], loading: guestsLoading } = useGuests()

  const loading = statsLoading || guestsLoading

  const attendingCounts = useMemo(() => {
    const rsvp = { coming: 0, not_coming: 0, unknown: 0 }
    let invites = 0
    let guests_total = 0
    let adults = 0
    let children = 0
    for (const guest of guests) {
      const additional = guest.additional_guests ?? []
      const kidCount = additional.filter((g) => g.type === 'child').length
      const adultCount = 1 + (additional.length - kidCount)
      const personCount = 1 + additional.length

      if (guest.rsvp_status === 'coming') {
        rsvp.coming += personCount
        invites++
        guests_total += personCount
        adults += adultCount
        children += kidCount
      } else if (guest.rsvp_status === 'not_coming') {
        rsvp.not_coming += personCount
      } else {
        rsvp.unknown += personCount
      }
    }
    return { rsvp, invites, guests_total, adults, children }
  }, [guests])

  const statCards = [
    {
      title: 'Anzahl Einladungen',
      value: guests.length,
      attendingValue: attendingCounts.invites,
      icon: IconMail,
      color: '#f7ca02',
    },
    {
      title: 'Gäste gesamt',
      value: stats?.total_guests ?? 0,
      attendingValue: attendingCounts.guests_total,
      icon: IconUsers,
      color: '#f7ca02',
    },
    {
      title: 'Erwachsene gesamt',
      value: stats?.total_adults ?? 0,
      attendingValue: attendingCounts.adults,
      icon: IconUserPlus,
      color: '#e5b802',
    },
    {
      title: 'Kinder gesamt',
      value: stats?.total_children ?? 0,
      attendingValue: attendingCounts.children,
      icon: IconMoodKid,
      color: '#bfb39b',
    },
  ]

  const colSpan = { base: 5, xs: 2, sm: 1, md: 1 }

  return (
    <Grid gutter="md" columns={5} grow>
      {statCards.map((stat) => {
        const Icon = stat.icon
        return (
          <Grid.Col key={stat.title} span={colSpan}>
            <Paper className={styles.statCard} p="md" shadow="sm">
              <Stack gap="xs" style={{ height: '100%' }} justify="space-between">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Text size="sm" c="dimmed" fw={500} className={styles.statTitle}>
                    {stat.title}
                  </Text>
                  <Icon size={20} style={{ color: stat.color, flexShrink: 0 }} />
                </Group>
                <Stack gap={2} align="center">
                  <Text
                    size="xl"
                    fw={700}
                    className={styles.statValue}
                    style={{ color: stat.color }}
                  >
                    {loading ? '...' : stat.value}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {loading ? '' : `davon ${stat.attendingValue} zusagen`}
                  </Text>
                </Stack>
              </Stack>
            </Paper>
          </Grid.Col>
        )
      })}

      {/* RSVP Status Card */}
      <Grid.Col span={colSpan}>
        <Paper className={styles.statCard} p="md" shadow="sm">
          <Stack gap="xs" style={{ height: '100%' }} justify="space-between">
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
              <Text size="sm" c="dimmed" fw={500} className={styles.statTitle}>
                RSVP-Status
              </Text>
              <IconClipboardList size={20} style={{ color: '#f7ca02', flexShrink: 0 }} />
            </Group>
            <Stack gap={4}>
              <Group gap="xs" justify="center" wrap="nowrap">
                <Text size="xs" c="dimmed" w={70}>Einladungen</Text>
                <Badge size="lg" color="green" variant="filled">
                  {loading ? '...' : (stats?.coming_count ?? 0)}
                </Badge>
                <Badge size="lg" color="red" variant="filled">
                  {loading ? '...' : (stats?.not_coming_count ?? 0)}
                </Badge>
                <Badge size="lg" color="gray" variant="filled">
                  {loading ? '...' : (stats?.unknown_count ?? 0)}
                </Badge>
              </Group>
              <Group gap="xs" justify="center" wrap="nowrap">
                <Text size="xs" c="dimmed" w={70}>Personen</Text>
                <Badge size="lg" color="green" variant="filled">
                  {loading ? '...' : attendingCounts.rsvp.coming}
                </Badge>
                <Badge size="lg" color="red" variant="filled">
                  {loading ? '...' : attendingCounts.rsvp.not_coming}
                </Badge>
                <Badge size="lg" color="gray" variant="filled">
                  {loading ? '...' : attendingCounts.rsvp.unknown}
                </Badge>
              </Group>
            </Stack>
          </Stack>
        </Paper>
      </Grid.Col>
    </Grid>
  )
}
